import styled from 'styled-components';
import { motion } from 'framer-motion';

const SkillsSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.background};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.gradientAlt};
    opacity: 0.03;
    transform: skewY(-6deg);
    transform-origin: top left;
  }
`;

const SkillsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.div)`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  h2 {
    ${({ theme }) => theme.mixins.headingStyles};
    font-size: ${({ theme }) => theme.sizes['2xl']};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.primary},
      ${({ theme }) => theme.secondary}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
    padding: 0.5rem 2rem;
    position: relative;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      padding: 2px;
      border-radius: 30px;
      background: linear-gradient(
        135deg,
        ${({ theme }) => theme.primary},
        ${({ theme }) => theme.secondary}
      );
      -webkit-mask: 
        linear-gradient(#fff 0 0) content-box, 
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      opacity: 0.5;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: scale(1.05);
      
      &::before {
        opacity: 1;
      }
    }
  }
`;


const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;
const CategoryTitle = styled.h3`
  ${({ theme }) => theme.mixins.headingStyles};
  font-size: ${({ theme }) => theme.sizes.xl};
  color: ${({ theme }) => theme.text};
  margin-bottom: 1.5rem;
  transition: color 0.3s ease;
`;
const SkillCategory = styled(motion.div)`
  background: ${({ theme }) => theme.card};
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadowMd};
  transition: all 0.4s ease-in-out;
  position: relative;
  overflow: hidden;
  border: 1px solid transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${({ theme }) => theme.primary};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadowLg};
    border-color: ${({ theme }) => `${theme.primary}30`};
    
    &::before {
      transform: scaleX(1);
    }

    ${CategoryTitle} {
      color: ${({ theme }) => theme.primary};
    }
  }
`;



const SkillsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SkillItem = styled(motion.li)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  color: ${({ theme }) => theme.text};
  ${({ theme }) => theme.mixins.bodyStyles};
  transition: all 0.3s ease;

  .skill-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => `${theme.primary}15`};
    border-radius: 8px;
    transition: all 0.3s ease;
    font-size: 1.2rem;
  }

  &:hover {
    transform: translateX(10px);
    color: ${({ theme }) => theme.primary};

    .skill-icon {
      background: ${({ theme }) => theme.primary};
      color: white;
      transform: rotate(10deg) scale(1.1);
    }
  }
`;


const Skills = () => {
    const skillCategories = [
        {
        title: "Frontend Development",
        skills: [
        { name: "React.js", icon: "⚛️" },
        { name: "Next.js", icon: "▲" },
        { name: "TypeScript", icon: "📘" },
        { name: "Tailwind CSS", icon: "🎨" },
        { name: "React Native", icon: "📱" }
        ]
        },
        {
        title: "Backend Development",
        skills: [
        
        { name: "Node.js", icon: "💚" },
        { name: "Express.js", icon: "🚂" }
        ]
        },
        {
        title: "Database & Tools",
        skills: [
        { name: "MongoDB", icon: "🍃" },
        { name: "Mysql", icon: "🐘" },
        { name: "Redis", icon: "📗" },
        { name: "GraphQL", icon: "◈" },
        { name: "Docker", icon: "🐳" }
        ]
        },
        {
        title: "Cloud & DevOps",
        skills: [
        { name: "AWS Services", icon: "☁️" },
        { name: "CI/CD", icon: "🔄" },
        { name: "Git", icon: "📚" },
        { name: "Linux", icon: "🐧" },
        { name: "Nginx", icon: "🌐" }
        ]
        }
        ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const categoryVariants = {
    hidden: { 
      opacity: 0,
      y: 30,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <SkillsSection id="skills">
      <SkillsContainer>
      <SectionTitle
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, type: "spring" }}
        >
          <h2>Technical Skills</h2>
        </SectionTitle>
        <SkillsGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {skillCategories.map((category, index) => (
            <SkillCategory
              key={index}
              variants={categoryVariants}
            >
              <CategoryTitle>{category.title}</CategoryTitle>
              <SkillsList>
                {category.skills.map((skill, skillIndex) => (
                  <SkillItem
                    key={skillIndex}
                    variants={itemVariants}
                  >
                    <span className="skill-icon">{skill.icon}</span>
                    {skill.name}
                  </SkillItem>
                ))}
              </SkillsList>
            </SkillCategory>
          ))}
        </SkillsGrid>
      </SkillsContainer>
    </SkillsSection>
  );
};

export default Skills;